import React from 'react';
import { SkeletonLine } from '@sequelae/lore-core';
import './DemoPage.css';
import './SectionPanel.css';
import './NavTest.css';

export const NavigationTestSkeleton: React.FC = () => (
  <>
    <div className="demo-page">
      <div className="demo-section" style={{ textAlign: 'center' }}>
        <div style={{ margin: '0 auto', width: '80%' }}>
          <SkeletonLine width="100%" height="1.5rem" />
        </div>
        <div style={{ margin: 'var(--spacing-8) auto 0', width: '90%' }}>
          <SkeletonLine width="100%" height="0.875rem" />
        </div>
      </div>
      <section className="section-panel-section">
        <div className="section-panel-eyebrow">
          <SkeletonLine width="40%" height="0.75rem" />
        </div>
        <div className="section-panel-panel">
          {Array.from({ length: 4 }).map((_, ri) => (
            <div
              key={ri}
              className={`section-panel-row${ri < 3 ? ' section-panel-row-with-border' : ''}`}
            >
              <span className="section-panel-row-icon">
                <SkeletonLine width="24px" height="24px" />
              </span>
              <span className="section-panel-row-label">
                <SkeletonLine width={`${50 + (ri * 13) % 30}%`} height="1rem" />
              </span>
            </div>
          ))}
        </div>
      </section>
      <div className="demo-section">
        <SkeletonLine width="45%" height="1.25rem" />
        <div className="demo-stack" style={{ marginTop: 'var(--spacing-8)' }}>
          {Array.from({ length: 5 }).map((_, bi) => (
            <SkeletonLine key={bi} width={`${70 + (bi * 11) % 25}%`} height="0.75rem" />
          ))}
        </div>
      </div>
    </div>
  </>
);

/**
 * Skeleton fallback for the Accessibility Demo page.
 * Mimics the simulator, typography samples, button demos, input demos, and instructions.
 */
export const AccessibilityDemoSkeleton: React.FC = () => (
  <>
    <div className="demo-page">
      {/* Font Size Simulator */}
      <div className="demo-section">
        <SkeletonLine width="60%" height="1.25rem" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-8)' }}>
          <SkeletonLine width="95%" height="0.75rem" />
          <SkeletonLine width="80%" height="0.75rem" />
        </div>
        <div style={{ marginTop: 'var(--spacing-16)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-12)' }}>
          <SkeletonLine width="100%" height="0.5rem" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <SkeletonLine width="100px" height="1.5rem" />
              <SkeletonLine width="180px" height="0.75rem" />
            </div>
          </div>
        </div>
      </div>

      {/* Font Scaling overview */}
      <div className="demo-section">
        <SkeletonLine width="45%" height="1.25rem" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-12)' }}>
          <SkeletonLine width="100%" height="0.75rem" />
          <SkeletonLine width="95%" height="0.75rem" />
          <SkeletonLine width="70%" height="0.75rem" />
        </div>
        <div style={{ marginTop: 'var(--spacing-12)' }}>
          <SkeletonLine width="100%" height="3rem" />
        </div>
      </div>

      {/* Button: Text Wrapping */}
      <div className="demo-section">
        <SkeletonLine width="55%" height="1.25rem" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-8)' }}>
          <SkeletonLine width="90%" height="0.75rem" />
          <SkeletonLine width="75%" height="0.75rem" />
        </div>
        <div className="demo-stack">
          <SkeletonLine width="120px" height="2.5rem" />
          <SkeletonLine width="280px" height="2.5rem" />
          <SkeletonLine width="100%" height="2.5rem" />
        </div>
      </div>

      {/* TextInput: Scaled Input */}
      <div className="demo-section">
        <SkeletonLine width="55%" height="1.25rem" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-8)' }}>
          <SkeletonLine width="85%" height="0.75rem" />
          <SkeletonLine width="60%" height="0.75rem" />
        </div>
        <div style={{ marginTop: 'var(--spacing-12)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <SkeletonLine width="100px" height="0.75rem" />
          <SkeletonLine width="100%" height="3rem" />
          <SkeletonLine width="220px" height="0.625rem" />
        </div>
      </div>

      {/* Typography Hierarchy */}
      <div className="demo-section">
        <SkeletonLine width="55%" height="1.25rem" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-8)' }}>
          <SkeletonLine width="90%" height="0.75rem" />
          <SkeletonLine width="70%" height="0.75rem" />
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-8)',
          padding: 'var(--spacing-16)',
          background: 'var(--surface-discrete-area)',
          borderRadius: 'var(--border-radius-radius-sm)',
          marginTop: 'var(--spacing-12)',
        }}>
          <SkeletonLine width="50%" height="2rem" />
          <SkeletonLine width="45%" height="1.5rem" />
          <SkeletonLine width="40%" height="1.25rem" />
          <SkeletonLine width="35%" height="1rem" />
          <SkeletonLine width="38%" height="0.875rem" />
          <SkeletonLine width="30%" height="0.75rem" />
        </div>
      </div>

      {/* Testing Instructions */}
      <div className="demo-section">
        <SkeletonLine width="55%" height="1.25rem" />
        <div className="demo-stack" style={{ marginTop: 'var(--spacing-8)' }}>
          {[0, 1, 2].map((i) => (
            <SkeletonLine
              key={i}
              width="100%"
              height="3.5rem"
            />
          ))}
        </div>
      </div>
    </div>
  </>
);
